<template>
  <transition :name="transitionName">
    <div 
      v-show="show && (!_.isEmpty(content.description) || !_.isEmpty(content.files) || !_.isEmpty(contentItems))" 
      class="scene-content" 
      :class="isSummary ? 'in-summary' : classes">
      <template v-if="content">
        <div class="title">
          {{ content.title }}
          <i class="el-icon-close" @click="toggle(content, false, true)"/>
        </div>
        <div class="scene-content-desc">
          <div v-if="content.description" v-html="content.description" class="markdown"/>

          <template v-if="!_.isEmpty(contentItems)">
            <div class="scene-content-items">
              <div 
                v-for="(item, index) in contentItems"
                :key="index"
                class="scene-content-item"
                :class="item.child.previewId ? `p__${item.child.previewId}` : ''"
                :style="`background-image: url(${item.parentPanorama.preview})`"
                @click="hanldeChildClick(item.child)">
                <div class="name">{{ item.title }}</div>
              </div>
            </div>
          </template>

          <template v-if="!_.isEmpty(content.files)">
            <div class="scene-content-medias">
              <div
                v-for="(file, fileIndex) in content.files"
                :key="file.id"
                class="scene-content-media"
                :class="{play: file.extension === 'mp4'}"
                @click="$emit('preview', content.files, fileIndex)">
                <video v-if="file.extension === 'mp4'" :src="file.url"/>
                <img v-else :src="file.url"/>
                <div class="name">{{ file.name }}</div>
              </div>
            </div>
          </template>
        </div>
      </template>
    </div>
  </transition>
</template>

<script>
let timeoutId = null

export default {
  name: 'Content',
  props: {
    hotspot: Object,
    isSummary: {
      type: Boolean,
      default: false
    }
  },
  inject: ['getCurrentMenuChildren', 'getPanoramaByUid'],
  watch: {
    hotspot: {
      handler(content) {
        this.setContent(content)
      },
      immediate: true
    }
  },
  computed: {
    transitionName() {
      if (this.transition) {
        return 'content'
      } else {
        return ''
      }
    }
  },
  data() {
    return {
      show: false,
      content: null,
      contentItems: null,
      transition: true,
      classes: null
    }
  },
  methods: {
    setContent(content) {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      if (!content || (this._.has(content, 'hidden') && !content.hidden)) {
        this.show = false
        this.content = null
        this.class = null
      } else {
        if (this.transition) {
          this.show = false
          timeoutId = setTimeout(() => {
            this.content = content
            this.show = true
          }, 200)
        } else {
          this.content = content
          this.show = true
        }

        // 动画衔接
        setTimeout(() => {
          this.classes = content.content_style ? `scene-content-${content.content_style}` : ''
        }, 200)

        if (content.type === 'list') {
          this.contentItems = this._.map(this._.sortBy(this.getCurrentMenuChildren(), 'weight'), child => {
            return {
              title: child.title,
              description: child.description,
              child: child,
              parentPanorama: this.getPanoramaByUid(child.panoramaUid)
            }
          })
        } else {
          this.contentItems = null
        }
      }
    },
    toggle() {
      this.show = !this.show
    },

    hanldeChildClick(child) {
      this.$emit('input', {name: 'hotspot', hotspot: child})
    }
  }
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

@mixin bottom-name {
  position: absolute;
  width: 100%;
  bottom: 0;
  padding: 6px;
  background-color: $color-black-half;
  font-size: 12px;
  color: #FFF;
  text-align: center;
}
.content-enter-active, .content-leave-active {
  transition: transform .2s, opacity .2s;
}
.content-enter, .content-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
.scene-content {
  position: fixed;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  background-color: #FFFFFF;
  border-radius: 5px;
  width: 460px;
  right: 10px;
  bottom: 10px;
  z-index: 1500;
  border: 1px solid $border-color-light;
  box-shadow: rgb(0 0 0 / 21%) 0px 4px 16px 0px;
  // height: calc(85vh - 300px);
  min-height: 200px;
  max-height: 70vh;
  .title {
    @include title;
    font-size: 16px;
    line-height: 27px;
  }
  .scene-content-items {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    grid-gap: 18px;
    margin-bottom: 15px;
    .scene-content-item {
      position: relative;
      background-size: 100%;
      background-repeat: no-repeat;
      background-position: center 0;
      cursor: pointer;
      &:before {
        content: "";
        float: left;
        padding-top: 75%; 
      }
      @each $index in 1,2,3,4,5 {
        &.p__#{$index} {
          background-position: center (0.1 * $index + 0.125) * 100% !important;
        }
      }
    }
    .name {
      @include bottom-name;
    }
  }
  .scene-content-medias {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    grid-gap: 18px;
    margin-bottom: 15px;
    // margin-top: 20px;
    .scene-content-media {
      position: relative;
      cursor: pointer;
      &:before {
        content: "";
        float: left;
        padding-top: 75%; 
      }
      video, img {
        position: absolute;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      &.play::after {
        content: '';
        display: block;
        position: absolute;
        width: 60px;
        height: 60px;
        left: calc(50% - 30px);
        top: calc(50% - 30px);
        background-size: 100%;
        background-position: center;
        background-repeat: no-repeat;
        z-index: 100;
        background-image: url('~@/assets/svg/play2.svg');
      }
    }
    .name {
      @include bottom-name;
    }
  }
  .scene-content-desc {
    width: 100%;
    overflow-y: auto;
    padding: 22px;
    overflow-x: hidden;
    @include scrollbar;
    color: $font-color-4;
  }
  h2 {
    padding: 15px 20px;
    font-size: 18px;
    margin-block: 0;
  }
  h3 {
    background-color: rgba(0,0,0,.1);
    font-size: 16px;
    margin-block: 0;
    color: $font-color-2;
    margin: 0 -20px 20px -20px;
    padding: 15px 20px;
  }
  &.in-summary {
    position: relative;
    background-color: #FFFFFF;
    height: 100%;
    width: 480px;
    max-width: none;
    border: none;
    box-shadow: none;
    right: auto; bottom: auto;
    max-height: none;
    min-height: none;
    .title {
      display: none;
    }
  }
  &.scene-content-2 {
    width: 60vw;
    height: calc(90vh - 100px);
    top: calc(5vh + 50px);
    left: 20vw;
    max-height: none;
    .scene-content-medias {
      grid-template-columns: repeat(4, 1fr);
    }
    .scene-content-items {
      grid-template-columns: repeat(4, 1fr);
    }
  }
  @media screen and (max-width: 1460px) {
    &.scene-content-2 {
      .scene-content-medias {
        grid-template-columns: repeat(3, 1fr);
      }
      .scene-content-items {
        grid-template-columns: repeat(3, 1fr);
      }
    }
  }
}
</style>